import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'glowlytix_reminders';

const FREQUENCIES = [
  { id: 'daily', label: 'Daily', days: 1 },
  { id: 'weekly', label: 'Weekly', days: 7 },
  { id: 'biweekly', label: 'Biweekly', days: 14 },
  { id: 'monthly', label: 'Monthly', days: 30 },
];

const DEFAULT_SETTINGS = {
  enabled: false,
  frequency: 'daily',
  hour: 20,
  minute: 0,
};

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(settings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

function msUntilNext(hour, minute, days) {
  const now = new Date();
  const next = new Date(now);
  next.setHours(hour, minute, 0, 0);
  if (next <= now) next.setDate(next.getDate() + 1);
  const base = next - now;
  // For multi-day frequencies, find the next aligned day from last-sent
  // For simplicity, always schedule from "next occurrence of the time today or tomorrow"
  // then use the SW to refire at interval
  return base + (days - 1) * 24 * 60 * 60 * 1000;
}

async function registerSW() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/reminder-sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;
    return reg;
  } catch {
    return null;
  }
}

export function useReminders() {
  const [settings, setSettings] = useState(loadSettings);
  const [permission, setPermission] = useState(
    () => ('Notification' in window ? Notification.permission : 'denied')
  );
  const [swReg, setSwReg] = useState(null);

  useEffect(() => {
    registerSW().then(setSwReg);
  }, []);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) return 'denied';
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, []);

  const scheduleWithSW = useCallback((reg, s) => {
    if (!reg || !reg.active) return;
    const freq = FREQUENCIES.find(f => f.id === s.frequency) || FREQUENCIES[0];
    const delay = msUntilNext(s.hour, s.minute, freq.days);
    reg.active.postMessage({
      type: 'SCHEDULE_REMINDER',
      title: 'Time to update your glow! ✨',
      body: 'Log today\'s skin, routine, and photos to track your progress.',
      delay,
    });
  }, []);

  const updateSettings = useCallback(async (partial) => {
    const next = { ...settings, ...partial };
    setSettings(next);
    saveSettings(next);

    if (next.enabled) {
      let perm = permission;
      if (perm === 'default') perm = await requestPermission();
      if (perm === 'granted' && swReg) {
        scheduleWithSW(swReg, next);
      }
    }
  }, [settings, permission, swReg, requestPermission, scheduleWithSW]);

  const enable = useCallback(async () => {
    let perm = permission;
    if (perm === 'default') perm = await requestPermission();
    if (perm !== 'granted') return false;
    await updateSettings({ enabled: true });
    return true;
  }, [permission, requestPermission, updateSettings]);

  const disable = useCallback(() => updateSettings({ enabled: false }), [updateSettings]);

  return {
    settings,
    permission,
    frequencies: FREQUENCIES,
    enable,
    disable,
    updateSettings,
  };
}
